// The bodies apps/secure's orientation routes take and answer, shared with
// the reader Worker that calls them. Nothing here names a child or a contact
// value: notes are free text the aide writes, which is why they live only in
// `enterprise` (orientation design Q17).
import * as z from "zod";
import { UUID_REGEX } from "../auth/authConstants";
import { ASSESSMENT_KINDS, type LinkSendStatus } from "./orientation";
import type { Assert, SameSet } from "./pin";
import { voiceChoicesSchema } from "./voice-consent";

export const SETUP_LINK_CHANNELS = ["email", "sms"] as const;
export type SetupLinkChannel = (typeof SETUP_LINK_CHANNELS)[number];

// Decided (Russ, 2026-10-04, Q13). Each send mints a fresh one-time token,
// so the per-session cap also bounds the credentials one call can create.
export const SETUP_LINK_LIMITS = {
  perSession: 3,
  minIntervalMs: 30_000,
  perAidePerDay: 20,
  globalPerDay: 200,
} as const;

export const SETUP_LINK_RATE_LIMITS = [
  "session",
  "interval",
  "aide",
  "global",
] as const;
export type SetupLinkRateLimit = (typeof SETUP_LINK_RATE_LIMITS)[number];

/** Who and why, never what: the link is the token's `link` claim. */
export const setupLinkSendBodySchema = z.strictObject({
  orientationSessionId: z.string().regex(UUID_REGEX),
  aideId: z.string().min(1).max(64),
  consentAffirmed: z.literal(true),
});
export type SetupLinkSendBody = z.infer<typeof setupLinkSendBodySchema>;

export const setupLinkSendReplySchema = z.object({
  status: z.literal("sent"),
  sessionSendCount: z.number().int().min(1),
});
export type SetupLinkSendReply = z.infer<typeof setupLinkSendReplySchema>;

/** The refusals the send route adds to apps/secure's error codes. */
export const SETUP_LINK_SEND_ERRORS = [
  "rate_limited",
  "opted_out",
  "not_deliverable",
  "unconfigured",
  "provider_error",
] as const;
export type SetupLinkSendError = (typeof SETUP_LINK_SEND_ERRORS)[number];

// Every refusal but these two is recorded under its own name as the
// session's linkSendStatus; these two are recorded as "failed".
type RecordedAsFailed = "not_deliverable" | "provider_error";
export type LinkSendStatusPin = Assert<
  SameSet<
    Exclude<SetupLinkSendError, RecordedAsFailed>,
    Exclude<LinkSendStatus, "sent" | "failed">
  >
>;

export const setupLinkRateLimitedSchema = z.object({
  error: z.literal("rate_limited"),
  limit: z.enum(SETUP_LINK_RATE_LIMITS),
});

export const ORIENTATION_NOTE_MAX_LENGTH = 2_000;
const MAX_ATTEMPT = 100;
const MAX_ITEM_INDEX = 999;

const assessmentSchema = z.enum(ASSESSMENT_KINDS);
const attemptSchema = z.number().int().min(1).max(MAX_ATTEMPT);

/** Which note: the session's, an assessment attempt's, or one item's. */
export const orientationNoteKeySchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("session") }),
  z.strictObject({
    kind: z.literal("assessment"),
    assessment: assessmentSchema,
    attempt: attemptSchema,
  }),
  z.strictObject({
    kind: z.literal("item"),
    assessment: assessmentSchema,
    attempt: attemptSchema,
    itemIndex: z.number().int().min(0).max(MAX_ITEM_INDEX),
  }),
]);
export type OrientationNoteKey = z.infer<typeof orientationNoteKeySchema>;

/** The stored form of a key, unique per session; see {@link parseNoteKey}. */
export function noteKeyString(key: OrientationNoteKey): string {
  switch (key.kind) {
    case "session":
      return "session";
    case "assessment":
      return `assessment:${key.assessment}:${key.attempt}`;
    case "item":
      return `item:${key.assessment}:${key.attempt}:${key.itemIndex}`;
  }
}

const STORED_KEY = /^(session|assessment|item)(?::([a-z]+):(\d+)(?::(\d+))?)?$/;

export function parseNoteKey(stored: string): OrientationNoteKey | null {
  const match = STORED_KEY.exec(stored);
  if (match === null) return null;
  const [, kind, assessment, attempt, itemIndex] = match;
  const candidate =
    kind === "session"
      ? { kind }
      : {
          kind,
          assessment,
          attempt: Number(attempt),
          ...(itemIndex !== undefined && { itemIndex: Number(itemIndex) }),
        };
  const parsed = orientationNoteKeySchema.safeParse(candidate);
  return parsed.success && noteKeyString(parsed.data) === stored
    ? parsed.data
    : null;
}

/** An empty body deletes the note, as clearing the field did in ../app. */
export const orientationNoteWriteSchema = z.strictObject({
  orientationSessionId: z.string().regex(UUID_REGEX),
  aideId: z.string().min(1).max(64),
  note: orientationNoteKeySchema,
  body: z.string().max(ORIENTATION_NOTE_MAX_LENGTH),
});
export type OrientationNoteWrite = z.infer<typeof orientationNoteWriteSchema>;

export const orientationNoteSchema = z.object({
  note: orientationNoteKeySchema,
  body: z.string(),
  updatedAt: z.number(),
});
export type OrientationNote = z.infer<typeof orientationNoteSchema>;

export const orientationNotesReplySchema = z.object({
  notes: z.array(orientationNoteSchema),
});

export const UNRECOGNIZED_CONSENT_DOCUMENT = "unrecognized";

/**
 * Whether a reader's study consent stands; never the signed name (R06).
 * `version` is the study document signed (`v1`), never the signed
 * version's voice parts: what voice follow may do now is `voice`, which
 * a withdrawal changes and the signed string does not.
 */
export const readerConsentReplySchema = z.object({
  consent: z
    .object({
      signedAt: z.number(),
      version: z.string(),
      voice: voiceChoicesSchema,
    })
    .nullable(),
});
export type ReaderConsentReply = z.infer<typeof readerConsentReplySchema>;
